import {
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { FacilityPicker } from "components/FacilityPicker";
import { DateField } from "components/Forms/DateField";
import { FacilityPickerFragment, NotReportingReason } from "generated-graphql/graphql";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormSetValue,
  useController,
} from "react-hook-form";
import * as yup from "yup";

export type ReasonFormInput = {
  selectedOption?: NotReportingReason;
  dateClosed?: string;
  dateInactive?: string;
  coLocatedId?: string;
  note?: string;
};

export function getValidationSchema(reportingYear: number) {
  // For validation, we need to check that dates are before the start of the reporting year
  // since the facility was closed/inactive for the ENTIRE reporting year
  const yearStartDate = `${reportingYear}-01-01`; // Jan 1 of the reporting year as ISO string

  // Base fields are `.nullable()` so a non-selected reason's field can hold `null`
  // (e.g. when switching reasons or clearing the facility picker) without yup throwing
  // a type error; the conditional `.required()` only applies to the selected reason.
  return yup.object().shape({
    selectedOption: yup.string().required("This field is required"),
    dateClosed: yup
      .string()
      .nullable()
      .when("selectedOption", {
        is: "CLOSED",
        then: yup
          .string()
          .nullable()
          .required("This field is required")
          .test(
            "date-closed-valid",
            `Date closed must be before Jan 1, ${reportingYear} (since the facility was closed for the entire reporting year)`,
            (value) => !!value && value < yearStartDate
          ),
      }),
    dateInactive: yup
      .string()
      .nullable()
      .when("selectedOption", {
        is: "INACTIVE",
        then: yup
          .string()
          .nullable()
          .required("This field is required")
          .test(
            "date-inactive-valid",
            `Date inactive must be before Jan 1, ${reportingYear} (since the facility was inactive for the entire reporting year)`,
            (value) => !!value && value < yearStartDate
          ),
      }),
    coLocatedId: yup
      .string()
      .nullable()
      .when("selectedOption", {
        is: "COLOCATED",
        then: yup.string().nullable().required("This field is required"),
      }),
    note: yup
      .string()
      .nullable()
      .when("selectedOption", {
        is: "EXEMPT",
        then: yup
          .string()
          .nullable()
          .trim()
          .required("A note is required when claiming exemption."),
      }),
  });
}

interface CoLocatedPickerProps {
  control: Control<ReasonFormInput>;
  tenantId: string;
  disabledFacilityIds: string[];
  coLocatedFacility: FacilityPickerFragment | null;
  setCoLocatedFacility: (fac: FacilityPickerFragment | null) => void;
  coLocatedLoading?: boolean;
  hasDefaultCoLocatedId?: boolean;
}

function CoLocatedPicker({
  control,
  tenantId,
  disabledFacilityIds,
  coLocatedFacility,
  setCoLocatedFacility,
  coLocatedLoading,
  hasDefaultCoLocatedId,
}: CoLocatedPickerProps) {
  // A co-located primary must NOT itself be co-located: Tier II reporting is
  // single-level (it doesn't roll a chain up to the root), so a chain C -> B -> A
  // mis-attributes reporting. It also can't be one of the facilities being marked
  // not-reporting (no co-located-to-self). "⁋" is encamp-omnisearch's
  // omnisearchStringSeparator, kept literal so the client bundle doesn't pull in the
  // server-only omnisearch barrel.
  const excludeTerm =
    disabledFacilityIds.length > 0 ? ` excludeIds:${disabledFacilityIds.join("⁋")}` : "";
  const searchTerm = `tenantId:${tenantId} isColocatedReporting:false${excludeTerm}`;
  return (
    <Controller
      name="coLocatedId"
      control={control}
      render={({ field, fieldState }) => (
        <FacilityPicker
          {...field}
          {...fieldState}
          disabled={!!hasDefaultCoLocatedId && !!coLocatedLoading}
          value={coLocatedFacility}
          defaultSearchTerm={searchTerm}
          label="Co-located Facility"
          hideColocatedReporting
          disabledFacilitiesById={disabledFacilityIds}
          onChange={(fac) => {
            setCoLocatedFacility(fac);
            field.onChange(fac?.id ?? null);
          }}
        />
      )}
    />
  );
}

function ExemptNoteField({ control }: { control: Control<ReasonFormInput> }) {
  return (
    <Controller
      name="note"
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          value={field.value ?? ""}
          error={!!fieldState.error}
          helperText={
            fieldState.error?.message ??
            "Explain why this facility is exempt (e.g. it is a research lab)."
          }
          label="Reason for Exemption"
          multiline
          minRows={2}
          fullWidth
          required
        />
      )}
    />
  );
}

interface NotReportingReasonFieldsProps {
  control: Control<ReasonFormInput>;
  reportingYear: number;
  tenantId: string;
  disabledFacilityIds: string[];
  coLocatedFacility: FacilityPickerFragment | null;
  setCoLocatedFacility: (fac: FacilityPickerFragment | null) => void;
  priorExemptNote?: string;
  setValue: UseFormSetValue<ReasonFormInput>;
  getValues: UseFormGetValues<ReasonFormInput>;
  /** Whether the coLocatedId default value is set and the picker query is loading */
  coLocatedLoading?: boolean;
  /** Whether the coLocatedId field has a default value (disables picker while loading) */
  hasDefaultCoLocatedId?: boolean;
  /**
   * Disables the co-located option (bulk-reset selections that span tenants can't
   * co-locate: a facility reports through another facility in the same tenant).
   */
  disableColocated?: boolean;
}

export function NotReportingReasonFields({
  control,
  reportingYear,
  tenantId,
  disabledFacilityIds,
  coLocatedFacility,
  setCoLocatedFacility,
  priorExemptNote,
  setValue,
  getValues,
  coLocatedLoading,
  hasDefaultCoLocatedId,
  disableColocated,
}: NotReportingReasonFieldsProps) {
  const reportingYearText = reportingYear.toString();
  const { field: selectedField } = useController({ name: "selectedOption", control });
  const selectedOption = selectedField.value;

  const handleReasonChange = (_event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    selectedField.onChange(value);
    // Carry the prior-year EXEMPT note forward the moment EXEMPT is chosen,
    // without clobbering a note the user has already entered. Done in this
    // change handler (not an effect) so it reacts to the user's selection.
    if (value === NotReportingReason.Exempt && priorExemptNote && !getValues("note")?.trim()) {
      setValue("note", priorExemptNote, { shouldValidate: true });
    }
  };

  return (
    <>
      <FormLabel component="legend">Select the reason this facility is not reporting:</FormLabel>
      <RadioGroup {...selectedField} onChange={handleReasonChange}>
        <FormControlLabel
          value={NotReportingReason.Closed}
          control={<Radio />}
          label={`This facility was closed for the entire reporting year (Jan 1, ${reportingYearText} through Dec 31, ${reportingYearText})`}
        />
        {selectedOption === NotReportingReason.Closed && (
          <DateField name="dateClosed" label="Date Closed" control={control} />
        )}

        <FormControlLabel
          value={NotReportingReason.Inactive}
          control={<Radio />}
          label={`This facility was inactive for the entire reporting year (Jan 1, ${reportingYearText} through Dec 31, ${reportingYearText})`}
        />
        {selectedOption === NotReportingReason.Inactive && (
          <DateField name="dateInactive" label="Date Inactive" control={control} />
        )}

        <FormControlLabel
          value={NotReportingReason.Colocated}
          control={<Radio />}
          disabled={disableColocated}
          label="This facility is co-located on the same site with another facility"
        />
        {disableColocated && (
          <FormHelperText>
            Co-located isn&apos;t available when the selection spans multiple organizations — a
            facility reports through another facility in the same organization.
          </FormHelperText>
        )}
        {selectedOption === NotReportingReason.Colocated && !disableColocated && (
          <CoLocatedPicker
            control={control}
            tenantId={tenantId}
            disabledFacilityIds={disabledFacilityIds}
            coLocatedFacility={coLocatedFacility}
            setCoLocatedFacility={setCoLocatedFacility}
            coLocatedLoading={coLocatedLoading}
            hasDefaultCoLocatedId={hasDefaultCoLocatedId}
          />
        )}

        <FormControlLabel
          value={NotReportingReason.UnderThreshold}
          control={<Radio />}
          label={`This facility's products were under the threshold for the entire reporting year (Jan 1, ${reportingYearText} through Dec 31, ${reportingYearText})`}
        />

        <FormControlLabel
          value={NotReportingReason.NotWithEncamp}
          control={<Radio />}
          label="This facility is not reporting through Encamp"
        />

        <FormControlLabel
          value={NotReportingReason.Exempt}
          control={<Radio />}
          label="This facility's on-site chemicals qualify for a regulatory exemption"
        />
        {selectedOption === NotReportingReason.Exempt && <ExemptNoteField control={control} />}
      </RadioGroup>
    </>
  );
}
